import { MultiDocumentPreview } from '@Components/Documents';
import { renderPaymentStateBadge } from '@Constants/TableColumns';
import { usePrintDocument, usePrintOptions } from '@Hooks/Documents';
import { type ApiInvoiceSnapshot, getEnumInvoiceTypeLabel } from '@Model';
import { Button, defaultIconSize, LoadingFrame } from '@synqo/frontend-component-library';
import { IconChevronLeft, IconChevronRight, IconDownload, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type PreviewRowsSource, usePreviewRows } from './invoiceSnapshotPreviewRows';
import styles from './InvoiceSnapshotQuickPreview.module.css';
import { INVOICE_SNAPSHOT_CATEGORY } from './useInvoiceSnapshotDocument';

/** Loads the next page once the shown invoice is this close to the end of the loaded ones. */
const PREFETCH_DISTANCE = 3;

const SHOW_ALL = 'Alle anzeigen';
const SHOW_SELECTION = 'Nur Auswahl anzeigen';

export interface InvoiceSnapshotQuickPreviewProps {
    /** The rows the list has loaded, which the preview follows as the list loads more. */
    source: PreviewRowsSource;
    /** The ticked rows. While any are loaded, the preview pages through them and `startId` until asked to show all. */
    selectedIds: string[];
    startId: string;
    onClose: () => void;
    onOpenDetail: (snapshotId: string) => void;
}

/**
 * The archived invoice beside the list. The chevrons and the arrow keys page through the list order, and the
 * next page of the list loads before the end of the loaded invoices is reached.
 */
// @ai-generated 2026-09-30T03:36:37+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function InvoiceSnapshotQuickPreview({
    source,
    selectedIds,
    startId,
    onClose,
    onOpenDetail,
}: InvoiceSnapshotQuickPreviewProps) {
    const { rows, total, fetchNextPage } = usePreviewRows(source);
    const shownIds = useMemo(() => [...selectedIds, startId], [selectedIds, startId]);
    const selectedRows = useMemo(() => rows.filter((row) => shownIds.includes(row.id)), [rows, shownIds]);
    const hasSelection = rows.some((row) => selectedIds.includes(row.id));
    const [wantsSelection, setWantsSelection] = useState(true);
    const onlySelection = hasSelection && wantsSelection;
    const [currentId, setCurrentId] = useState(startId);

    const shown = onlySelection ? selectedRows : rows;
    const index = Math.max(
        shown.findIndex((row) => row.id === currentId),
        0,
    );
    const snapshot = shown[index];
    const count = onlySelection ? shown.length : Math.max(total, rows.length);
    const canLoadMore = !onlySelection && rows.length < total;

    useEffect(() => {
        if (canLoadMore && index >= rows.length - PREFETCH_DISTANCE) {
            fetchNextPage?.();
        }
    }, [canLoadMore, index, rows, fetchNextPage]);

    const { printOptions, isLoading } = usePrintOptions(
        INVOICE_SNAPSHOT_CATEGORY,
        snapshot?.id ?? '',
        snapshot !== undefined,
    );
    const printOption = printOptions[0];
    const printDocument = usePrintDocument();

    const showAt = useCallback(
        (target: number) => {
            const row = shown[target];
            if (row !== undefined) {
                setCurrentId(row.id);
            }
        },
        [shown],
    );
    const showPrevious = useCallback(() => showAt(index - 1), [showAt, index]);
    const showNext = useCallback(() => showAt(index + 1), [showAt, index]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (isTypingTarget(event.target)) {
                return;
            }
            if (event.key === 'ArrowLeft') {
                showPrevious();
            }
            if (event.key === 'ArrowRight') {
                showNext();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showPrevious, showNext]);

    const download = () => {
        if (printOption === undefined) {
            return;
        }
        printDocument({ printOption: printOption.id, primaryId: printOption.related_object_id, mode: 'download' });
    };

    if (snapshot === undefined) {
        return (
            <div className={styles.panel}>
                <span className={styles.muted}>Keine Rechnung zum Anzeigen.</span>
            </div>
        );
    }

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <span className={styles.title}>{`${getEnumInvoiceTypeLabel(snapshot.type)} ${snapshot.number}`}</span>
                {renderPaymentStateBadge(snapshot)}
                <div className={styles.actions}>
                    {hasSelection && (
                        <Button
                            appearance="transparent"
                            label={onlySelection ? SHOW_ALL : SHOW_SELECTION}
                            onClick={() => {
                                setCurrentId(
                                    onlySelection ? snapshot.id : tickedOrNearest(rows, shownIds, snapshot.id),
                                );
                                setWantsSelection(!onlySelection);
                            }}
                        >
                            <span className={styles.scopeLabel}>
                                <span className={onlySelection ? undefined : styles.hiddenLabel}>{SHOW_ALL}</span>
                                <span className={onlySelection ? styles.hiddenLabel : undefined}>{SHOW_SELECTION}</span>
                            </span>
                        </Button>
                    )}
                    <Button
                        appearance="outlined"
                        type="square"
                        label="Vorherige Rechnung"
                        locked={index === 0}
                        onClick={showPrevious}
                    >
                        <IconChevronLeft size={defaultIconSize} />
                    </Button>
                    <span
                        className={styles.position}
                        style={{ minWidth: `${2 * String(Math.max(total, rows.length)).length + 5}ch` }}
                    >
                        {`${index + 1} von ${count}`}
                    </span>
                    <Button
                        appearance="outlined"
                        type="square"
                        label="Nächste Rechnung"
                        locked={index >= shown.length - 1}
                        onClick={showNext}
                    >
                        <IconChevronRight size={defaultIconSize} />
                    </Button>
                    <Button appearance="filled" variant="success" locked={printOption === undefined} onClick={download}>
                        <IconDownload size={defaultIconSize} />
                        &nbsp;Herunterladen
                    </Button>
                    <Button appearance="transparent" type="square" label="Vorschau schließen" onClick={onClose}>
                        <IconX size={defaultIconSize} />
                    </Button>
                </div>
            </div>
            <div className={styles.document}>
                {printOption !== undefined && (
                    <MultiDocumentPreview
                        key={snapshot.id}
                        printOption={printOption}
                        primaryId={printOption.related_object_id}
                    />
                )}
                {printOption === undefined && isLoading && <LoadingFrame />}
                {printOption === undefined && !isLoading && (
                    <span className={styles.muted}>Zu dieser Rechnung liegt kein Dokument vor.</span>
                )}
            </div>
            <div className={styles.footer}>
                <Button appearance="transparent" onClick={() => onOpenDetail(snapshot.id)}>
                    Zur Detailseite
                </Button>
            </div>
        </div>
    );
}

/** `id` when it is ticked or nothing is, else the next ticked row after it in list order, else the one before. */
// @ai-generated 2026-10-05T13:53:41+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function tickedOrNearest(rows: ApiInvoiceSnapshot[], selectedIds: string[], id: string): string {
    const isTicked = (row: ApiInvoiceSnapshot) => selectedIds.includes(row.id);
    const at = rows.findIndex((row) => row.id === id);
    if (!rows.some(isTicked) || (at !== -1 && isTicked(rows[at]))) {
        return id;
    }

    const after = rows.slice(at + 1).find(isTicked);
    const before = rows.slice(0, at).reverse().find(isTicked);
    return (after ?? before)?.id ?? id;
}

// @ai-generated 2026-10-01T14:03:06+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function isTypingTarget(target: EventTarget | null): boolean {
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
    );
}
